import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import Layout from '@/components/Layout';
import CourseMetadataForm from '@/components/CourseMetadataForm';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Checkbox } from '@/components/ui/checkbox';
import { PlusCircle, Edit, Trash2, Users as UsersIcon, Download, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL } from '../../config/api';
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('lms_token')}`
});

// Data & Analytics Tab Component
function DataAnalyticsTab({ courseId, courseName }) {
  const [assignedStudents, setAssignedStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingCSV, setDownloadingCSV] = useState(false);

  useEffect(() => {
    fetchAssignedStudentsData();
  }, [courseId]);

  const fetchAssignedStudentsData = async () => {
    try {
      setLoading(true);
      // Get course details to fetch assigned student IDs
      const courseRes = await axios.get(`${API}/courses/${courseId}`, {
        headers: getAuthHeaders()
      });
      
      const studentIds = courseRes.data.students_assigned || [];
      
      if (studentIds.length === 0) {
        setAssignedStudents([]);
        setLoading(false);
        return;
      }

      // Calculate total lessons in the course as fallback
      const modules = courseRes.data.modules || [];
      let courseTotalLessons = 0;
      for (const module of modules) {
        if (module.lessons) {
          courseTotalLessons += module.lessons.length;
        }
      }

      // Fetch all students and filter assigned ones
      const studentsRes = await axios.get(`${API}/users`, {
        headers: getAuthHeaders()
      });
      
      const allStudents = studentsRes.data.filter(user => 
        studentIds.includes(user.id) && user.role === 'student'
      );

      // Fetch progress for each assigned student using the admin endpoint
      const studentsWithProgress = await Promise.all(
        allStudents.map(async (student) => {
          try {
            // Use the admin endpoint that accepts user_id as a path parameter
            // Add cache busting parameter to force fresh data
            const progressRes = await axios.get(
              `${API}/admin/progress/course/${courseId}/user/${student.id}?_t=${Date.now()}`,
              { 
                headers: {
                  ...getAuthHeaders(),
                  'Cache-Control': 'no-cache',
                  'Pragma': 'no-cache'
                }
              }
            );
            
            // Use API total_lessons, but fallback to course total if API returns 0 or undefined
            // Use strict checking to avoid treating 0 as falsy
            const totalLessons = (progressRes.data.total_lessons !== undefined && progressRes.data.total_lessons !== null && progressRes.data.total_lessons > 0) 
              ? progressRes.data.total_lessons 
              : courseTotalLessons;
            
            return {
              ...student,
              progress_percentage: progressRes.data.progress_percentage || progressRes.data.completion_percentage || 0,
              completed_lessons: progressRes.data.lessons_completed || progressRes.data.completed_lessons || 0,
              total_lessons: totalLessons
            };
          } catch (error) {
            console.error(`Error fetching progress for student ${student.id}:`, error);
            console.error('Error details:', error.response?.data);
            return {
              ...student,
              progress_percentage: 0,
              completed_lessons: 0,
              total_lessons: courseTotalLessons // Use course total as fallback
            };
          }
        })
      );

      setAssignedStudents(studentsWithProgress);
    } catch (error) {
      console.error('Failed to fetch assigned students data:', error);
      toast.error('Failed to load student data');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAccess = async (studentId, studentName) => {
    if (!window.confirm(`Are you sure you want to remove ${studentName} from this course?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/courses/${courseId}/students/${studentId}`, {
        headers: getAuthHeaders()
      });
      
      toast.success(`${studentName} has been removed from the course`);
      fetchAssignedStudentsData(); // Refresh the list
    } catch (error) {
      console.error('Failed to remove student:', error);
      toast.error('Failed to remove student access');
    }
  };

  const handleDownloadCSV = () => {
    try {
      setDownloadingCSV(true);
      
      // Prepare CSV data
      const headers = ['Student Name', 'Email ID', 'Grade', 'Role', 'Course Progress (%)', 'Lessons Completed'];
      const rows = assignedStudents.map(student => [
        `${student.first_name} ${student.last_name}`,
        student.email,
        student.grade_year || student.grade || 'N/A',
        student.role,
        student.progress_percentage.toFixed(2),
        `${student.completed_lessons}/${student.total_lessons}`
      ]);

      // Create CSV content
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `${courseName || 'course'}_students_report_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Report downloaded successfully!');
    } catch (error) {
      console.error('Failed to download CSV:', error);
      toast.error('Failed to download report');
    } finally {
      setDownloadingCSV(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-0 card-shadow bg-white rounded-2xl">
        <CardContent className="p-8 text-center">
          <div className="flex items-center justify-center space-x-2">
            <div className="w-6 h-6 border-4 border-primary-blue border-t-transparent rounded-full animate-spin"></div>
            <p className="text-[#4D4D4D]">Loading student data...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="border-0 card-shadow bg-white rounded-2xl">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <BarChart3 className="w-6 h-6 text-primary-blue" />
                <CardTitle className="text-xl md:text-2xl font-bold text-navy">Data & Analytics</CardTitle>
              </div>
              <p className="text-sm text-[#4D4D4D] font-light">
                View performance and manage access for all students assigned to this course
              </p>
            </div>
            <Button
              onClick={handleDownloadCSV}
              disabled={assignedStudents.length === 0 || downloadingCSV}
              className="bg-green-600 hover:bg-green-700 text-white rounded-xl transition-smooth btn-press"
            >
              <Download className="w-4 h-4 mr-2" />
              {downloadingCSV ? 'Downloading...' : 'Download Report'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {assignedStudents.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <UsersIcon className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-navy mb-2">No Students Assigned</h3>
              <p className="text-[#4D4D4D] mb-6">
                Assign students to this course to view their progress and analytics
              </p>
              <Button
                onClick={() => {
                  const tabsList = document.querySelector('[value="students"]');
                  if (tabsList) tabsList.click();
                }}
                className="bg-primary-blue hover:bg-[#1874CD] text-white rounded-xl transition-smooth btn-press"
              >
                Go to Assign Students
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-4 px-4 text-sm font-semibold text-navy">Student Name</th>
                    <th className="text-left py-4 px-4 text-sm font-semibold text-navy">Email ID</th>
                    <th className="text-left py-4 px-4 text-sm font-semibold text-navy">Grade</th>
                    <th className="text-left py-4 px-4 text-sm font-semibold text-navy">Role</th>
                    <th className="text-left py-4 px-4 text-sm font-semibold text-navy">Course Progress</th>
                    <th className="text-left py-4 px-4 text-sm font-semibold text-navy">Lessons Completed</th>
                    <th className="text-center py-4 px-4 text-sm font-semibold text-navy">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {assignedStudents.map((student, index) => (
                    <tr 
                      key={student.id} 
                      className={`border-b border-gray-100 hover:bg-gray-50 transition-smooth ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                    >
                      <td className="py-4 px-4">
                        <div className="font-semibold text-navy">
                          {student.first_name} {student.last_name}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="text-sm text-[#4D4D4D]">{student.email}</div>
                      </td>
                      <td className="py-4 px-4">
                        <span className="inline-block px-2 py-1 text-xs font-medium bg-purple-100 text-purple-700 rounded">
                          {student.grade_year || student.grade || 'N/A'}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <span className="inline-block px-3 py-1 text-xs font-semibold bg-blue-100 text-blue-700 rounded-full">
                          {student.role}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center space-x-3">
                          <div className="flex-1">
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-primary-blue h-2 rounded-full transition-all" 
                                style={{ width: `${student.progress_percentage}%` }}
                              ></div>
                            </div>
                          </div>
                          <span className="text-sm font-semibold text-navy min-w-[3rem] text-right">
                            {student.progress_percentage.toFixed(0)}%
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="text-sm font-medium text-navy">
                          {student.completed_lessons} / {student.total_lessons}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <Button
                          onClick={() => handleRemoveAccess(student.id, `${student.first_name} ${student.last_name}`)}
                          variant="outline"
                          size="sm"
                          className="border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 rounded-xl"
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Remove
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {assignedStudents.length > 0 && (
            <div className="mt-6 p-4 bg-blue-50 rounded-xl">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center space-x-6">
                  <div>
                    <p className="text-sm text-[#4D4D4D]">Total Students</p>
                    <p className="text-2xl font-bold text-navy">{assignedStudents.length}</p>
                  </div>
                  <div>
                    <p className="text-sm text-[#4D4D4D]">Average Progress</p>
                    <p className="text-2xl font-bold text-primary-blue">
                      {(assignedStudents.reduce((sum, s) => sum + s.progress_percentage, 0) / assignedStudents.length).toFixed(1)}%
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-[#4D4D4D]">Completed Course</p>
                    <p className="text-2xl font-bold text-green-600">
                      {assignedStudents.filter(s => s.progress_percentage >= 100).length}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function CourseBuilder({ user, onLogout }) {
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [students, setStudents] = useState([]);
  const [assignedStudentIds, setAssignedStudentIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details');
  const [courseDetailsComplete, setCourseDetailsComplete] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  
  // Student filtering states
  const [selectAll, setSelectAll] = useState(false);
  const [nameFilter, setNameFilter] = useState('');
  const [schoolFilter, setSchoolFilter] = useState('');
  const [gradeFilter, setGradeFilter] = useState('');
  const [coursesForFilter, setCoursesForFilter] = useState([]);
  const [completedCourseFilter, setCompletedCourseFilter] = useState('all');
  const [filteredStudents, setFilteredStudents] = useState([]);

  useEffect(() => {
    fetchCourseData();
  }, [courseId]);

  // Filter students based on current filters
  useEffect(() => {
    const filterStudents = async () => {
      let filtered = [...students];

      // Name filter
      if (nameFilter) {
        filtered = filtered.filter(student =>
          `${student.first_name} ${student.last_name}`.toLowerCase().includes(nameFilter.toLowerCase())
        );
      }

      // School filter
      if (schoolFilter) {
        filtered = filtered.filter(student =>
          student.school_name?.toLowerCase().includes(schoolFilter.toLowerCase())
        );
      }

      // Grade filter
      if (gradeFilter) {
        filtered = filtered.filter(student =>
          student.grade_year?.toLowerCase().includes(gradeFilter.toLowerCase())
        );
      }

      // Course completed filter - FIXED: Use correct endpoint and ensure 100% completion
      if (completedCourseFilter && completedCourseFilter !== 'all') {
        try {
          const studentsWithProgress = await Promise.all(
            filtered.map(async (student) => {
              try {
                // FIXED: Use correct admin endpoint
                const progressRes = await axios.get(
                  `${API}/admin/progress/course/${completedCourseFilter}/user/${student.id}`,
                  { headers: getAuthHeaders() }
                );
                const progressPercentage = progressRes.data.progress_percentage || progressRes.data.completion_percentage || 0;
                return {
                  ...student,
                  courseProgress: progressPercentage
                };
              } catch (error) {
                console.error(`Error fetching progress for student ${student.id}:`, error);
                return { ...student, courseProgress: 0 };
              }
            })
          );
          // FIXED: Only show students with exactly 100% completion
          filtered = studentsWithProgress.filter(student => student.courseProgress >= 100);
          console.log(`Course Completed Filter: Found ${filtered.length} students with 100% completion`);
        } catch (error) {
          console.error('Error filtering by completed course:', error);
        }
      }

      setFilteredStudents(filtered);
    };

    filterStudents();
  }, [students, nameFilter, schoolFilter, gradeFilter, completedCourseFilter]);

  const fetchCourseData = async () => {
    try {
      const [courseRes, modulesRes, studentsRes, coursesRes] = await Promise.all([
        axios.get(`${API}/courses/${courseId}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/modules/${courseId}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/users`, { headers: getAuthHeaders() }),
        axios.get(`${API}/courses`, { headers: getAuthHeaders() })
      ]);

      setCourse(courseRes.data);
      setAssignedStudentIds(courseRes.data.students_assigned || []);

      const modulesWithLessons = await Promise.all(
        modulesRes.data.map(async (module) => {
          try {
            const lessonsRes = await axios.get(`${API}/lessons/${module.id}`, { headers: getAuthHeaders() });
            console.log(`Fetched ${lessonsRes.data.length} lessons for module ${module.id}`);
            
            const lessonsWithQuizzes = await Promise.all(
              (lessonsRes.data || []).map(async (lesson) => {
                try {
                  const quizRes = await axios.get(`${API}/quizzes/lesson/${lesson.id}`, { headers: getAuthHeaders() });
                  return { ...lesson, quiz: quizRes.data };
                } catch (quizError) {
                  console.log(`No quiz for lesson ${lesson.id}`);
                  return { ...lesson, quiz: null };
                }
              })
            );
            return { ...module, lessons: lessonsWithQuizzes };
          } catch (lessonError) {
            console.error(`Failed to fetch lessons for module ${module.id}:`, lessonError);
            return { ...module, lessons: [] };
          }
        })
      );

      console.log('Modules with lessons:', modulesWithLessons);
      setModules(modulesWithLessons);
      setStudents(studentsRes.data.filter((u) => u.role === 'student'));
      setCoursesForFilter(coursesRes.data.filter(c => c.id !== courseId));
    } catch (error) {
      console.error('Failed to fetch course data:', error);
      toast.error('Failed to load course data');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignStudents = async () => {
    try {
      // Get the original list of assigned students from the course data
      const originallyAssignedIds = course?.students_assigned || [];
      
      // Find only the NEWLY selected students (not previously assigned)
      const newStudentIds = assignedStudentIds.filter(id => !originallyAssignedIds.includes(id));
      
      if (newStudentIds.length === 0) {
        toast.info('No new students selected to assign');
        return;
      }
      
      // Only send the newly selected students to the backend
      await axios.post(`${API}/courses/${courseId}/assign`, newStudentIds, { headers: getAuthHeaders() });
      toast.success(`Course assigned to ${newStudentIds.length} new student${newStudentIds.length > 1 ? 's' : ''} successfully! ${newStudentIds.length === 1 ? 'They can' : 'They can'} now see this course in their dashboard.`);
      fetchCourseData(); // Refresh to update assigned students
    } catch (error) {
      console.error('Failed to assign students:', error);
      toast.error('Failed to assign students');
    }
  };

  const toggleStudent = (studentId) => {
    setAssignedStudentIds((prev) =>
      prev.includes(studentId) ? prev.filter((id) => id !== studentId) : [...prev, studentId]
    );
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      const filteredStudentIds = filteredStudents.map(student => student.id);
      setAssignedStudentIds(prev => [...new Set([...prev, ...filteredStudentIds])]);
    } else {
      const filteredStudentIds = filteredStudents.map(student => student.id);
      setAssignedStudentIds(prev => prev.filter(id => !filteredStudentIds.includes(id)));
    }
  };

  const clearFilters = () => {
    setNameFilter('');
    setSchoolFilter('');
    setGradeFilter('');
    setCompletedCourseFilter('all');
    setSelectAll(false);
  };

  if (loading) {
    return <Layout user={user} onLogout={onLogout} currentPage="courses"><div className="p-8">Loading...</div></Layout>;
  }

  const handleSaveCourseMetadata = async (metadataFormData) => {
    try {
      await axios.put(`${API}/courses/${courseId}`, metadataFormData, { headers: getAuthHeaders() });
      toast.success('Course details saved successfully! Proceeding to add course content...');
      setCourseDetailsComplete(true);
      fetchCourseData(); // Refresh to get updated course data
      
      // Auto-navigate to Course Content tab after 1.5 seconds
      setTimeout(() => {
        setActiveTab('content');
        toast.info('Now add modules and lessons to your course');
      }, 1500);
    } catch (error) {
      console.error('Failed to save course metadata:', error);
      toast.error('Failed to save course details');
    }
  };

  const handleSaveTillNow = async () => {
    setSaveLoading(true);
    try {
      // Update only the is_published field
      await axios.put(`${API}/courses/${courseId}`, { is_published: false }, { headers: getAuthHeaders() });
      toast.success('Course saved as draft! Content preserved for future editing.');
      fetchCourseData(); // Refresh course data
    } catch (error) {
      console.error('Failed to save course:', error);
      toast.error('Failed to save course');
    } finally {
      setSaveLoading(false);
    }
  };

  const handlePublishCourse = async () => {
    // Check minimum requirements
    if (modules.length === 0) {
      toast.error('Please add at least one module before publishing');
      return;
    }

    // Check if at least one module has lessons
    const hasLessons = modules.some(module => module.lessons && module.lessons.length > 0);
    if (!hasLessons) {
      toast.error('Please add at least one lesson to publish the course');
      return;
    }

    setPublishLoading(true);
    try {
      // Update only the is_published field
      await axios.put(`${API}/courses/${courseId}`, { is_published: true }, { headers: getAuthHeaders() });
      toast.success('Course published successfully! Students can now access it.');
      fetchCourseData(); // Refresh course data
    } catch (error) {
      console.error('Failed to publish course:', error);
      toast.error('Failed to publish course');
    } finally {
      setPublishLoading(false);
    }
  };

  return (
    <Layout user={user} onLogout={onLogout} currentPage="courses">
      <div className="p-6 lg:p-8 space-y-6" data-testid="course-builder">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold text-navy">{course?.title || 'Course Builder'}</h1>
          <p className="text-[#4D4D4D] mt-2 font-light">{course?.description || 'Build and manage your course'}</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-4xl grid-cols-4 bg-[#F7F7F7] rounded-xl p-1">
            <TabsTrigger value="details" data-testid="details-tab" className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-primary-blue transition-smooth">
              <div className="flex items-center space-x-2">
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${activeTab === 'details' ? 'bg-primary-blue text-white' : courseDetailsComplete ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600'}`}>
                  {courseDetailsComplete ? '✓' : '1'}
                </span>
                <span>Course Details</span>
              </div>
            </TabsTrigger>
            <TabsTrigger 
              value="content" 
              data-testid="content-tab" 
              className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-primary-blue transition-smooth"
            >
              <div className="flex items-center space-x-2">
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${activeTab === 'content' ? 'bg-primary-blue text-white' : modules.length > 0 ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600'}`}>
                  {modules.length > 0 ? '✓' : '2'}
                </span>
                <span>Course Content</span>
              </div>
            </TabsTrigger>
            <TabsTrigger 
              value="students" 
              data-testid="students-tab" 
              className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-primary-blue transition-smooth"
            >
              <div className="flex items-center space-x-2">
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${activeTab === 'students' ? 'bg-primary-blue text-white' : assignedStudentIds.length > 0 ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600'}`}>
                  {assignedStudentIds.length > 0 ? '✓' : '3'}
                </span>
                <span>Assign Students</span>
              </div>
            </TabsTrigger>
            <TabsTrigger 
              value="analytics" 
              data-testid="analytics-tab" 
              className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-primary-blue transition-smooth"
            >
              <div className="flex items-center space-x-2">
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${activeTab === 'analytics' ? 'bg-primary-blue text-white' : 'bg-gray-300 text-gray-600'}`}>
                  4
                </span>
                <span>Data & Analytics</span>
              </div>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="mt-6">
            <CourseMetadataForm courseData={course} onSave={handleSaveCourseMetadata} />
          </TabsContent>

          <TabsContent value="content" className="mt-6">
            <div className="space-y-4">
              <ModuleSection courseId={courseId} modules={modules} onRefresh={fetchCourseData} />
              
              {modules.length > 0 && (
                <Card className="border-0 card-shadow bg-primary-blue/5 rounded-2xl">
                  <CardContent className="p-6 text-center">
                    <p className="text-navy font-semibold mb-4">
                      Great! You've added {modules.length} module{modules.length > 1 ? 's' : ''} to your course.
                    </p>
                    <Button
                      onClick={() => {
                        setActiveTab('students');
                        toast.info('Now assign students to your course');
                      }}
                      className="bg-primary-blue hover:bg-[#1874CD] text-white rounded-xl transition-smooth btn-press px-8"
                    >
                      Continue to Assign Students →
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Save and Publish Buttons */}
              <Card className="border-0 card-shadow bg-white rounded-2xl">
                <CardContent className="p-6">
                  <div className="flex justify-center space-x-4">
                    <Button
                      onClick={handleSaveTillNow}
                      disabled={saveLoading}
                      variant="outline"
                      className="border-2 border-primary-blue text-primary-blue hover:bg-primary-blue/10 rounded-xl transition-smooth px-8"
                    >
                      {saveLoading ? 'Saving...' : 'Save Till Now'}
                    </Button>
                    <Button
                      onClick={handlePublishCourse}
                      disabled={publishLoading || modules.length === 0}
                      className="bg-green-600 hover:bg-green-700 text-white rounded-xl transition-smooth btn-press px-8"
                    >
                      {publishLoading ? 'Publishing...' : 'Publish'}
                    </Button>
                  </div>
                  {modules.length === 0 && (
                    <p className="text-sm text-[#4D4D4D] text-center mt-2">
                      Add at least one module with lessons to publish
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="students" className="mt-6">
            <div className="space-y-6">
              <Card className="border-0 card-shadow bg-white rounded-2xl">
                <CardHeader>
                  <CardTitle className="text-xl font-bold text-navy">Assign Students to Course</CardTitle>
                  <p className="text-sm text-[#4D4D4D] font-light mt-2">
                    Select students who should have access to this course. Only assigned students will see this course in their dashboard.
                  </p>
                </CardHeader>
                <CardContent>
                  {/* Filters Section */}
                  <div className="mb-6 p-4 bg-gray-50 rounded-xl space-y-4">
                    <h3 className="font-semibold text-navy">Filter Students</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div>
                        <Label htmlFor="nameFilter" className="text-sm font-medium">Name</Label>
                        <Input
                          id="nameFilter"
                          value={nameFilter}
                          onChange={(e) => setNameFilter(e.target.value)}
                          placeholder="Search by name..."
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="schoolFilter" className="text-sm font-medium">School</Label>
                        <Input
                          id="schoolFilter"
                          value={schoolFilter}
                          onChange={(e) => setSchoolFilter(e.target.value)}
                          placeholder="Search by school..."
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="gradeFilter" className="text-sm font-medium">Grade</Label>
                        <Input
                          id="gradeFilter"
                          value={gradeFilter}
                          onChange={(e) => setGradeFilter(e.target.value)}
                          placeholder="Search by grade..."
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="courseFilter" className="text-sm font-medium">Course Completed</Label>
                        <Select value={completedCourseFilter} onValueChange={setCompletedCourseFilter}>
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select course..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Students</SelectItem>
                            {coursesForFilter.map(course => (
                              <SelectItem key={course.id} value={course.id}>{course.title}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Button 
                        onClick={clearFilters} 
                        variant="outline" 
                        size="sm"
                        className="text-sm"
                      >
                        Clear Filters
                      </Button>
                      <span className="text-sm text-gray-600 flex items-center">
                        Showing {filteredStudents.length} of {students.length} students
                      </span>
                    </div>
                  </div>

                  {/* Assignment Status Info */}
                  {course?.students_assigned && course.students_assigned.length > 0 && (
                    <div className="mb-4 p-4 rounded-xl bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200">
                      <div className="flex items-center gap-2 mb-2">
                        <UsersIcon className="w-5 h-5 text-green-600" />
                        <p className="font-bold text-green-700">Current Assignment Status</p>
                      </div>
                      <p className="text-sm text-green-800">
                        <span className="font-semibold">{course.students_assigned.length} student{course.students_assigned.length > 1 ? 's' : ''}</span> already assigned to this course.
                        {assignedStudentIds.length > 0 && (
                          <>
                            {' '}You have selected <span className="font-semibold">{assignedStudentIds.filter(id => !course.students_assigned.includes(id)).length} new student{assignedStudentIds.filter(id => !course.students_assigned.includes(id)).length !== 1 ? 's' : ''}</span> to add.
                          </>
                        )}
                      </p>
                    </div>
                  )}

                  {/* Select All Checkbox */}
                  <div className="flex items-center space-x-3 p-3 rounded-xl bg-blue-50 mb-4">
                    <Checkbox
                      checked={selectAll}
                      onCheckedChange={handleSelectAll}
                      data-testid="select-all-checkbox"
                    />
                    <Label className="font-semibold text-navy">Select All Filtered Students</Label>
                    {filteredStudents.length > 0 && (
                      <span className="text-sm text-primary-blue">
                        ({filteredStudents.length} students)
                      </span>
                    )}
                  </div>

                  {/* Students List */}
                  <div className="space-y-3 mb-6 max-h-96 overflow-y-auto">
                    {filteredStudents.length === 0 ? (
                      <p className="text-[#4D4D4D] text-center py-8">
                        {students.length === 0 ? 'No students available' : 'No students match the current filters'}
                      </p>
                    ) : (
                      filteredStudents.map((student) => {
                        const isAlreadyAssigned = course?.students_assigned?.includes(student.id);
                        return (
                          <div key={student.id} className={`flex items-center space-x-3 p-3 rounded-xl transition-smooth hover:bg-[#E7E7E7] ${isAlreadyAssigned ? 'bg-green-50 border-2 border-green-200' : 'bg-[#F7F7F7]'}`}>
                            <Checkbox
                              checked={assignedStudentIds.includes(student.id)}
                              onCheckedChange={() => toggleStudent(student.id)}
                              data-testid={`student-checkbox-${student.id}`}
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className="font-semibold text-navy">{student.first_name} {student.last_name}</p>
                                {isAlreadyAssigned && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700 border border-green-300">
                                    ✓ Already Assigned
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-[#4D4D4D]">{student.email}</p>
                              {(student.school_name || student.grade_year) && (
                                <p className="text-xs text-gray-500">
                                  {student.school_name}{student.school_name && student.grade_year ? ' • ' : ''}{student.grade_year}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  <Button
                    onClick={handleAssignStudents}
                    disabled={assignedStudentIds.length === 0}
                    className="w-full bg-primary-blue hover:bg-[#1874CD] text-white font-semibold py-4 px-4 rounded-xl transition-smooth btn-press"
                    data-testid="assign-students-button"
                  >
                    {(() => {
                      if (assignedStudentIds.length === 0) return 'Select Students to Assign';
                      const originallyAssignedIds = course?.students_assigned || [];
                      const newStudentIds = assignedStudentIds.filter(id => !originallyAssignedIds.includes(id));
                      const alreadyAssignedCount = assignedStudentIds.length - newStudentIds.length;
                      
                      if (newStudentIds.length === 0) {
                        return `${assignedStudentIds.length} Student${assignedStudentIds.length > 1 ? 's' : ''} Already Assigned`;
                      }
                      return `Assign ${newStudentIds.length} New Student${newStudentIds.length > 1 ? 's' : ''} ${alreadyAssignedCount > 0 ? `(${alreadyAssignedCount} already assigned)` : ''}`;
                    })()}
                  </Button>
                </CardContent>
              </Card>

              {assignedStudentIds.length > 0 && (
                <Card className="border-0 card-shadow bg-green-50 rounded-2xl">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-3xl text-white">✓</span>
                    </div>
                    <h3 className="text-xl font-bold text-navy mb-2">Course Setup Complete!</h3>
                    <p className="text-[#4D4D4D] mb-4">
                      Your course is now live and accessible to {assignedStudentIds.length} student{assignedStudentIds.length > 1 ? 's' : ''}. 
                      Students can now see this course in their "My Courses" section.
                    </p>
                    <div className="flex justify-center space-x-3">
                      <Button
                        onClick={() => window.location.href = '/courses'}
                        variant="outline"
                        className="border-2 border-primary-blue text-primary-blue rounded-xl hover:bg-primary-blue/10 transition-smooth"
                      >
                        Back to My Courses
                      </Button>
                      <Button
                        onClick={() => setActiveTab('content')}
                        className="bg-primary-blue hover:bg-[#1874CD] text-white rounded-xl transition-smooth btn-press"
                      >
                        Edit Course Content
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          <TabsContent value="analytics" className="mt-6">
            <DataAnalyticsTab courseId={courseId} courseName={course?.title} />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

function ModuleSection({ courseId, modules, onRefresh }) {
  const [showCreateModule, setShowCreateModule] = useState(false);
  const [newModule, setNewModule] = useState({ title: '', order: modules.length + 1 });

  const handleCreateModule = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/modules`, { course_id: courseId, ...newModule }, { headers: getAuthHeaders() });
      toast.success('Module created successfully!');
      setShowCreateModule(false);
      setNewModule({ title: '', order: modules.length + 2 });
      onRefresh();
    } catch (error) {
      toast.error('Failed to create module');
    }
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-navy">Modules</h2>
        <Dialog open={showCreateModule} onOpenChange={setShowCreateModule}>
          <DialogTrigger asChild>
            <Button className="bg-primary-blue hover:bg-[#1874CD] text-white rounded-xl transition-smooth btn-press" data-testid="add-module-button">
              <PlusCircle className="w-4 h-4 mr-2" strokeWidth={2} />
              Add Module
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#E8F3FF] rounded-2xl shadow-2xl border-0">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-navy">Create New Module</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateModule} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="module-title" className="text-[15px] font-semibold text-navy">Module Title</Label>
                <Input
                  id="module-title"
                  value={newModule.title}
                  onChange={(e) => setNewModule({ ...newModule, title: e.target.value })}
                  required
                  className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth bg-white"
                  data-testid="module-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="module-order" className="text-[15px] font-semibold text-navy">Order</Label>
                <Input
                  id="module-order"
                  type="number"
                  value={newModule.order}
                  onChange={(e) => setNewModule({ ...newModule, order: parseInt(e.target.value) })}
                  required
                  className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth bg-white"
                  data-testid="module-order-input"
                />
              </div>
              <Button type="submit" className="w-full bg-primary-blue hover:bg-[#1874CD] text-white rounded-xl h-12 transition-smooth btn-press" data-testid="submit-module-button">
                Create Module
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {modules.length === 0 ? (
        <Card className="border-0 card-shadow bg-white rounded-2xl">
          <CardContent className="p-12 text-center text-[#4D4D4D]">
            <p className="font-light">No modules yet. Create your first module to get started.</p>
          </CardContent>
        </Card>
      ) : (
        <Accordion type="single" collapsible className="space-y-3">
          {modules.map((module, index) => (
            <AccordionItem key={module.id} value={module.id} className="border-0 card-shadow bg-white rounded-2xl px-4">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center justify-between w-full pr-4">
                  <span className="font-bold text-navy">Module {index + 1}: {module.title}</span>
                  <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
                    <ModuleEditButton module={module} onRefresh={onRefresh} />
                    <ModuleDeleteButton module={module} onRefresh={onRefresh} />
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <LessonSection moduleId={module.id} lessons={module.lessons || []} onRefresh={onRefresh} />
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </>
  );
}

function LessonSection({ moduleId, lessons, onRefresh }) {
  const [showCreateLesson, setShowCreateLesson] = useState(false);
  const [newLesson, setNewLesson] = useState({ title: '', video_url: '', order: lessons.length + 1 });

  const handleCreateLesson = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/lessons`, { module_id: moduleId, ...newLesson }, { headers: getAuthHeaders() });
      toast.success('Lesson created successfully!');
      setShowCreateLesson(false);
      setNewLesson({ title: '', video_url: '', order: lessons.length + 2 });
      onRefresh();
    } catch (error) {
      toast.error('Failed to create lesson');
    }
  };

  return (
    <div className="space-y-3 mt-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-navy">Lessons</h3>
        <Dialog open={showCreateLesson} onOpenChange={setShowCreateLesson}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-primary-blue hover:bg-[#1874CD] text-white rounded-xl transition-smooth" data-testid="add-lesson-button">
              <PlusCircle className="w-4 h-4 mr-1" strokeWidth={2} />
              Add Lesson
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#E8F3FF] rounded-2xl shadow-2xl border-0">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-navy">Create New Lesson</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateLesson} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="lesson-title" className="text-[15px] font-semibold text-navy">Lesson Title</Label>
                <Input
                  id="lesson-title"
                  value={newLesson.title}
                  onChange={(e) => setNewLesson({ ...newLesson, title: e.target.value })}
                  required
                  className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth bg-white"
                  data-testid="lesson-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="video-url" className="text-[15px] font-semibold text-navy">YouTube Video URL</Label>
                <Input
                  id="video-url"
                  value={newLesson.video_url}
                  onChange={(e) => setNewLesson({ ...newLesson, video_url: e.target.value })}
                  placeholder="https://www.youtube.com/watch?v=..."
                  required
                  className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth bg-white"
                  data-testid="lesson-video-url-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lesson-order" className="text-[15px] font-semibold text-navy">Order</Label>
                <Input
                  id="lesson-order"
                  type="number"
                  value={newLesson.order}
                  onChange={(e) => setNewLesson({ ...newLesson, order: parseInt(e.target.value) })}
                  required
                  className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth bg-white"
                  data-testid="lesson-order-input"
                />
              </div>
              <Button type="submit" className="w-full bg-primary-blue hover:bg-[#1874CD] text-white rounded-xl h-12 transition-smooth btn-press" data-testid="submit-lesson-button">
                Create Lesson
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {lessons.length === 0 ? (
        <p className="text-sm text-[#4D4D4D] text-center py-4 font-light">No lessons yet</p>
      ) : (
        <div className="space-y-2">
          {lessons.map((lesson, index) => (
            <div key={lesson.id} className="p-3 rounded-xl bg-[#F7F7F7]">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-sm text-navy">Lesson {index + 1}: {lesson.title}</p>
                  <p className="text-xs text-[#4D4D4D] truncate max-w-md">{lesson.video_url}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <QuizButton lesson={lesson} onRefresh={onRefresh} />
                  <AssignmentButton lesson={lesson} onRefresh={onRefresh} />
                  <LessonEditButton lesson={lesson} onRefresh={onRefresh} />
                  <LessonDeleteButton lesson={lesson} onRefresh={onRefresh} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function QuizButton({ lesson, onRefresh }) {
  const [showQuizDialog, setShowQuizDialog] = useState(false);
  const [questions, setQuestions] = useState([{ question: '', type: 'mcq', options: ['', '', '', ''], correct_answer: '' }]);
  const [isEdit, setIsEdit] = useState(false);

  // Load existing quiz data when dialog opens
  useEffect(() => {
    if (showQuizDialog && lesson.quiz) {
      setIsEdit(true);
      // Load existing quiz questions
      if (lesson.quiz.questions && lesson.quiz.questions.length > 0) {
        setQuestions(lesson.quiz.questions);
      }
    } else if (showQuizDialog && !lesson.quiz) {
      setIsEdit(false);
      setQuestions([{ question: '', type: 'mcq', options: ['', '', '', ''], correct_answer: '' }]);
    }
  }, [showQuizDialog, lesson.quiz]);

  const handleCreateQuiz = async (e) => {
    e.preventDefault();
    try {
      if (isEdit && lesson.quiz) {
        // Update existing quiz
        await axios.put(`${API}/quizzes/${lesson.quiz.id}`, {
          lesson_id: lesson.id,
          questions,
          pass_percentage: 30
        }, { headers: getAuthHeaders() });
        toast.success('Quiz updated successfully!');
      } else {
        // Create new quiz
        await axios.post(`${API}/quizzes`, {
          lesson_id: lesson.id,
          questions,
          pass_percentage: 30
        }, { headers: getAuthHeaders() });
        toast.success('Quiz created successfully!');
      }
      setShowQuizDialog(false);
      onRefresh();
    } catch (error) {
      console.error('Quiz error:', error);
      toast.error(isEdit ? 'Failed to update quiz' : 'Failed to create quiz');
    }
  };

  const addQuestion = () => {
    setQuestions([...questions, { question: '', type: 'mcq', options: ['', '', '', ''], correct_answer: '' }]);
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...questions];
    updated[index][field] = value;
    setQuestions(updated);
  };

  const updateOption = (qIndex, oIndex, value) => {
    const updated = [...questions];
    updated[qIndex].options[oIndex] = value;
    setQuestions(updated);
  };

  const removeQuestion = (index) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  return (
    <Dialog open={showQuizDialog} onOpenChange={setShowQuizDialog}>
      <DialogTrigger asChild>
        <Button 
          size="sm" 
          variant={lesson.quiz ? 'outline' : 'default'} 
          className={lesson.quiz ? 'border-2 border-primary-blue text-primary-blue hover:bg-primary-blue/10 rounded-xl transition-smooth' : 'bg-primary-blue hover:bg-[#1874CD] text-white rounded-xl transition-smooth'}
          data-testid={`quiz-button-${lesson.id}`}
        >
          {lesson.quiz ? 'Edit Quiz' : 'Add Quiz'}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#E8F3FF] max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl border-0">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-navy">Create Quiz for: {lesson.title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleCreateQuiz} className="space-y-6">
          {questions.map((q, qIndex) => (
            <Card key={qIndex} className="border-0 card-shadow bg-white rounded-2xl">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-[15px] font-semibold text-navy">Question {qIndex + 1}</Label>
                  {questions.length > 1 && (
                    <Button type="button" size="sm" variant="destructive" onClick={() => removeQuestion(qIndex)} className="rounded-lg">
                      <Trash2 className="w-3 h-3" strokeWidth={2} />
                    </Button>
                  )}
                </div>
                <Textarea
                  value={q.question}
                  onChange={(e) => updateQuestion(qIndex, 'question', e.target.value)}
                  placeholder="Enter question"
                  required
                  className="border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth"
                  data-testid={`quiz-question-${qIndex}`}
                />
                <div className="space-y-2">
                  <Label className="text-[15px] font-semibold text-navy">Question Type</Label>
                  <Select value={q.type} onValueChange={(value) => updateQuestion(qIndex, 'type', value)}>
                    <SelectTrigger className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth" data-testid={`quiz-type-${qIndex}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mcq">Multiple Choice</SelectItem>
                      <SelectItem value="true_false">True/False</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {q.type === 'mcq' ? (
                  <div className="space-y-2">
                    <Label className="text-[15px] font-semibold text-navy">Options</Label>
                    {q.options.map((opt, oIndex) => (
                      <Input
                        key={oIndex}
                        value={opt}
                        onChange={(e) => updateOption(qIndex, oIndex, e.target.value)}
                        placeholder={`Option ${oIndex + 1}`}
                        required
                        className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth"
                        data-testid={`quiz-option-${qIndex}-${oIndex}`}
                      />
                    ))}
                  </div>
                ) : null}
                <div className="space-y-2">
                  <Label className="text-[15px] font-semibold text-navy">Correct Answer</Label>
                  {q.type === 'mcq' ? (
                    <Select 
                      value={q.correct_answer} 
                      onValueChange={(value) => updateQuestion(qIndex, 'correct_answer', value)}
                    >
                      <SelectTrigger className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth" data-testid={`quiz-correct-answer-${qIndex}`}>
                        <SelectValue placeholder="Select correct option" />
                      </SelectTrigger>
                      <SelectContent>
                        {q.options
                          .map((opt, oIndex) => ({ opt, oIndex }))
                          .filter(({ opt }) => opt && opt.trim() !== '')
                          .map(({ opt, oIndex }) => (
                            <SelectItem 
                              key={oIndex} 
                              value={opt}
                            >
                              Option {oIndex + 1}: {opt}
                            </SelectItem>
                          ))}
                        {q.options.filter(opt => opt && opt.trim() !== '').length === 0 && (
                          <div className="px-2 py-1.5 text-sm text-[#A5A5A5]">Please fill in options first</div>
                        )}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Select 
                      value={q.correct_answer} 
                      onValueChange={(value) => updateQuestion(qIndex, 'correct_answer', value)}
                    >
                      <SelectTrigger className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth" data-testid={`quiz-correct-answer-${qIndex}`}>
                        <SelectValue placeholder="Select True or False" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="True">True</SelectItem>
                        <SelectItem value="False">False</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
          <div className="flex space-x-3">
            <Button type="button" onClick={addQuestion} variant="outline" className="flex-1 border-2 border-[#E7E7E7] rounded-xl hover:border-primary-blue transition-smooth" data-testid="add-question-button">
              <PlusCircle className="w-4 h-4 mr-2" strokeWidth={2} />
              Add Question
            </Button>
            <Button type="submit" className="flex-1 bg-primary-blue hover:bg-[#1874CD] text-white rounded-xl transition-smooth btn-press" data-testid="submit-quiz-button">
              {isEdit ? 'Update Quiz' : 'Create Quiz'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AssignmentButton({ lesson, onRefresh }) {
  const [showAssignmentDialog, setShowAssignmentDialog] = useState(false);
  const [assignment, setAssignment] = useState({
    title: '',
    description: '',
    assignment_type: 'text',
    is_mandatory: false
  });
  const [isEdit, setIsEdit] = useState(false);

  // Load existing assignment data when dialog opens
  useEffect(() => {
    const loadAssignment = async () => {
      if (showAssignmentDialog) {
        try {
          const response = await axios.get(`${API}/assignments/lesson/${lesson.id}`, {
            headers: getAuthHeaders()
          });
          if (response.data.length > 0) {
            setIsEdit(true);
            setAssignment(response.data[0]);
          } else {
            setIsEdit(false);
            setAssignment({
              title: '',
              description: '',
              assignment_type: 'text',
              is_mandatory: false
            });
          }
        } catch (error) {
          console.error('Error loading assignment:', error);
          setIsEdit(false);
          setAssignment({
            title: '',
            description: '',
            assignment_type: 'text',
            is_mandatory: false
          });
        }
      }
    };
    loadAssignment();
  }, [showAssignmentDialog, lesson.id]);

  const handleCreateAssignment = async (e) => {
    e.preventDefault();
    try {
      const assignmentData = {
        ...assignment,
        lesson_id: lesson.id
      };

      if (isEdit && assignment.id) {
        await axios.put(`${API}/assignments/${assignment.id}`, assignmentData, {
          headers: getAuthHeaders()
        });
        toast.success('Assignment updated successfully!');
      } else {
        await axios.post(`${API}/assignments`, assignmentData, {
          headers: getAuthHeaders()
        });
        toast.success('Assignment created successfully!');
      }

      setShowAssignmentDialog(false);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error saving assignment:', error);
      toast.error('Failed to save assignment');
    }
  };

  const handleDeleteAssignment = async () => {
    if (!assignment.id) return;
    
    try {
      await axios.delete(`${API}/assignments/${assignment.id}`, {
        headers: getAuthHeaders()
      });
      toast.success('Assignment deleted successfully!');
      setShowAssignmentDialog(false);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error deleting assignment:', error);
      toast.error('Failed to delete assignment');
    }
  };

  return (
    <Dialog open={showAssignmentDialog} onOpenChange={setShowAssignmentDialog}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="border-2 border-[#E7E7E7] text-purple-600 hover:text-purple-700 hover:border-purple-600 transition-smooth rounded-xl"
          data-testid={`assignment-button-${lesson.id}`}
        >
          📝 Assignment
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#E8F3FF] rounded-2xl shadow-2xl border-0">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-navy">
            {isEdit ? 'Edit Assignment' : 'Create Assignment'} for: {lesson.title}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleCreateAssignment} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="assignment-title" className="text-[15px] font-semibold text-navy">Assignment Title</Label>
            <Input
              id="assignment-title"
              value={assignment.title}
              onChange={(e) => setAssignment({ ...assignment, title: e.target.value })}
              required
              className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth bg-white"
              placeholder="Enter assignment title"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="assignment-description" className="text-[15px] font-semibold text-navy">Description/Instructions</Label>
            <Textarea
              id="assignment-description"
              value={assignment.description}
              onChange={(e) => setAssignment({ ...assignment, description: e.target.value })}
              required
              rows={3}
              className="border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth bg-white"
              placeholder="Enter assignment instructions"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-[15px] font-semibold text-navy">Assignment Type</Label>
            <div className="space-y-3">
              {[
                { value: 'text', label: '📝 Text Submission', desc: 'Student submits text answer' },
                { value: 'video_link', label: '🎥 Video Link Submission', desc: 'Student submits video URL' },
                { value: 'image_upload', label: '🖼️ Image Upload Submission', desc: 'Student uploads image file' }
              ].map((type) => (
                <div key={type.value} className="flex items-center space-x-3">
                  <input
                    type="radio"
                    id={type.value}
                    name="assignment_type"
                    value={type.value}
                    checked={assignment.assignment_type === type.value}
                    onChange={(e) => setAssignment({ ...assignment, assignment_type: e.target.value })}
                    className="text-primary-blue"
                  />
                  <Label htmlFor={type.value} className="flex-1 cursor-pointer">
                    <div className="text-[15px] font-semibold text-navy">{type.label}</div>
                    <div className="text-xs text-[#4D4D4D]">{type.desc}</div>
                  </Label>
                </div>
              ))}
            </div>
          </div>
          <div className="flex items-center space-x-3 p-3 bg-yellow-50 rounded-xl border-2 border-yellow-200">
            <input
              type="checkbox"
              id="mandatory-toggle"
              checked={assignment.is_mandatory}
              onChange={(e) => setAssignment({ ...assignment, is_mandatory: e.target.checked })}
              className="text-primary-blue"
            />
            <Label htmlFor="mandatory-toggle" className="cursor-pointer">
              <div className="text-[15px] font-semibold text-navy">📌 Mandatory Assignment</div>
              <div className="text-xs text-[#4D4D4D]">
                {assignment.is_mandatory 
                  ? 'Students must complete this assignment to proceed to next lesson' 
                  : 'Assignment is optional - students can proceed without completing it'
                }
              </div>
            </Label>
          </div>
          <div className="flex space-x-3">
            {isEdit && assignment.id && (
              <Button
                type="button"
                onClick={handleDeleteAssignment}
                variant="destructive"
                className="flex-1 rounded-xl"
              >
                Delete Assignment
              </Button>
            )}
            <Button
              type="submit"
              className="flex-1 bg-primary-blue hover:bg-[#1874CD] text-white rounded-xl h-12 transition-smooth btn-press"
            >
              {isEdit ? 'Update Assignment' : 'Create Assignment'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Edit/Delete Components
function ModuleEditButton({ module, onRefresh }) {
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editModule, setEditModule] = useState({ title: '', order: 1 });

  useEffect(() => {
    if (showEditDialog && module) {
      setEditModule({ title: module.title, order: module.order });
    }
  }, [showEditDialog, module]);

  const handleUpdateModule = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/modules/${module.id}`, editModule, { headers: getAuthHeaders() });
      toast.success('Module updated successfully!');
      setShowEditDialog(false);
      onRefresh();
    } catch (error) {
      toast.error('Failed to update module');
    }
  };

  return (
    <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="p-2 rounded-lg">
          <Edit className="w-4 h-4" strokeWidth={2} />
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#E8F3FF] rounded-2xl shadow-2xl border-0">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-navy">Edit Module</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleUpdateModule} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-module-title" className="text-[15px] font-semibold text-navy">Module Title</Label>
            <Input
              id="edit-module-title"
              value={editModule.title}
              onChange={(e) => setEditModule({ ...editModule, title: e.target.value })}
              required
              className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth bg-white"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-module-order" className="text-[15px] font-semibold text-navy">Order</Label>
            <Input
              id="edit-module-order"
              type="number"
              value={editModule.order}
              onChange={(e) => setEditModule({ ...editModule, order: parseInt(e.target.value) })}
              required
              className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth bg-white"
            />
          </div>
          <Button type="submit" className="w-full bg-primary-blue hover:bg-[#1874CD] text-white rounded-xl h-12 transition-smooth btn-press">
            Update Module
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ModuleDeleteButton({ module, onRefresh }) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleDeleteModule = async () => {
    try {
      await axios.delete(`${API}/modules/${module.id}`, { headers: getAuthHeaders() });
      toast.success('Module deleted successfully!');
      setShowDeleteDialog(false);
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete module');
    }
  };

  return (
    <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="p-2 rounded-lg text-red-600 hover:text-red-700">
          <Trash2 className="w-4 h-4" strokeWidth={2} />
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#E8F3FF] rounded-2xl shadow-2xl border-0">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-navy">Delete Module</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-[#4D4D4D]">
            Are you sure you want to delete "{module.title}"? This will permanently delete all lessons, quizzes, and assignments within this module. This action cannot be undone.
          </p>
          <div className="flex space-x-3">
            <Button onClick={() => setShowDeleteDialog(false)} variant="outline" className="flex-1 rounded-xl">
              Cancel
            </Button>
            <Button onClick={handleDeleteModule} variant="destructive" className="flex-1 rounded-xl">
              Delete Module
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LessonEditButton({ lesson, onRefresh }) {
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editLesson, setEditLesson] = useState({ title: '', video_url: '', order: 1 });

  useEffect(() => {
    if (showEditDialog && lesson) {
      setEditLesson({ title: lesson.title, video_url: lesson.video_url, order: lesson.order });
    }
  }, [showEditDialog, lesson]);

  const handleUpdateLesson = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/lessons/${lesson.id}`, editLesson, { headers: getAuthHeaders() });
      toast.success('Lesson updated successfully!');
      setShowEditDialog(false);
      onRefresh();
    } catch (error) {
      toast.error('Failed to update lesson');
    }
  };

  return (
    <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="p-2 rounded-lg">
          <Edit className="w-4 h-4" strokeWidth={2} />
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#E8F3FF] rounded-2xl shadow-2xl border-0">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-navy">Edit Lesson</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleUpdateLesson} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-lesson-title" className="text-[15px] font-semibold text-navy">Lesson Title</Label>
            <Input
              id="edit-lesson-title"
              value={editLesson.title}
              onChange={(e) => setEditLesson({ ...editLesson, title: e.target.value })}
              required
              className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth bg-white"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-video-url" className="text-[15px] font-semibold text-navy">YouTube Video URL</Label>
            <Input
              id="edit-video-url"
              value={editLesson.video_url}
              onChange={(e) => setEditLesson({ ...editLesson, video_url: e.target.value })}
              placeholder="https://www.youtube.com/watch?v=..."
              required
              className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth bg-white"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-lesson-order" className="text-[15px] font-semibold text-navy">Order</Label>
            <Input
              id="edit-lesson-order"
              type="number"
              value={editLesson.order}
              onChange={(e) => setEditLesson({ ...editLesson, order: parseInt(e.target.value) })}
              required
              className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth bg-white"
            />
          </div>
          <Button type="submit" className="w-full bg-primary-blue hover:bg-[#1874CD] text-white rounded-xl h-12 transition-smooth btn-press">
            Update Lesson
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function LessonDeleteButton({ lesson, onRefresh }) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleDeleteLesson = async () => {
    try {
      await axios.delete(`${API}/lessons/${lesson.id}`, { headers: getAuthHeaders() });
      toast.success('Lesson deleted successfully!');
      setShowDeleteDialog(false);
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete lesson');
    }
  };

  return (
    <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="p-2 rounded-lg text-red-600 hover:text-red-700">
          <Trash2 className="w-4 h-4" strokeWidth={2} />
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#E8F3FF] rounded-2xl shadow-2xl border-0">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-navy">Delete Lesson</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-[#4D4D4D]">
            Are you sure you want to delete "{lesson.title}"? This will permanently delete the lesson, its quiz, and any assignments. This action cannot be undone.
          </p>
          <div className="flex space-x-3">
            <Button onClick={() => setShowDeleteDialog(false)} variant="outline" className="flex-1 rounded-xl">
              Cancel
            </Button>
            <Button onClick={handleDeleteLesson} variant="destructive" className="flex-1 rounded-xl">
              Delete Lesson
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );


}